<template>
  <b-row>
    <!-- LEFT COLUMN: icon, title, description -->
    <b-col xs="12" md="6">
      <b-card>
        <b-col cols="12">
          <b-row>
            <b-col cols="12" class="heading-icon">
              <i :class="model.icon || 'fa-solid fa-envelope'"></i>
              <h4>{{ $translateLabel(model.label) }}</h4>
            </b-col>
            <b-col cols="12">
              <p>
                Send emails seamlessly through your workflows with zero configuration.
                Each tenant sends from their own isolated subdomain with automatic branding.
              </p>
            </b-col>
          </b-row>
        </b-col>

        <StartTrialFooter :integration="model" />
      </b-card>
    </b-col>

    <!-- RIGHT COLUMN: integration info when active -->
    <b-col sm="12" md="6">
      <b-card v-if="active">
        <b-col cols="12">
          <b-row>
            <b-col cols="12" class="heading-icon">
              <h5>Mail Configuration</h5>
            </b-col>

            <b-col cols="12" class="mt-3">
              <!-- Sender Email Info -->
              <b-form-group label="Sender Email" label-class="font-weight-bold">
                <b-form-input
                  :value="senderEmail"
                  readonly
                  @focus="$event.target.select()"
                />
                <b-form-text>
                  Emails are sent from this address. The format includes your tenant UUID for isolation.
                </b-form-text>
              </b-form-group>

              <!-- Current Plan -->
              <b-form-group label="Current Plan" label-class="font-weight-bold">
                <b-form-input
                  :value="currentPlan"
                  readonly
                />
                <b-form-text>
                  Your subscription tier determines rate limits and content policies.
                </b-form-text>
              </b-form-group>

              <!-- Rate Limits Info -->
              <b-form-group label="Rate Limits" label-class="font-weight-bold">
                <b-list-group>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    Emails per hour
                    <b-badge variant="primary" pill>{{ rateLimits.hourly || '-' }}</b-badge>
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    Emails per day
                    <b-badge variant="primary" pill>{{ rateLimits.daily || '-' }}</b-badge>
                  </b-list-group-item>
                </b-list-group>
              </b-form-group>

              <!-- Content Limits Info -->
              <b-form-group label="Content Limits" label-class="font-weight-bold">
                <b-list-group>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    Max recipients per email
                    <b-badge variant="info" pill>{{ contentLimits.maxRecipients || '-' }}</b-badge>
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    Max body size
                    <b-badge variant="info" pill>{{ formatBytes(contentLimits.maxBodySize) || '-' }}</b-badge>
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    Max attachments
                    <b-badge variant="info" pill>{{ contentLimits.maxAttachments || '-' }}</b-badge>
                  </b-list-group-item>
                  <b-list-group-item class="d-flex justify-content-between align-items-center">
                    Max attachment size
                    <b-badge variant="info" pill>{{ formatBytes(contentLimits.maxAttachmentSize) || '-' }}</b-badge>
                  </b-list-group-item>
                </b-list-group>
              </b-form-group>
            </b-col>
          </b-row>
        </b-col>

        <b-col cols="12" class="mt-3">
          <b-alert variant="info" show>
            <strong>Zero Configuration Required</strong><br>
            Protokol Mail works out of the box. Simply use the email node in your workflows
            or call the API to send emails. All rate limits and content policies are
            automatically enforced based on your subscription tier.
          </b-alert>
        </b-col>
      </b-card>
    </b-col>
  </b-row>
</template>

<style scoped>
.heading-icon {
  text-align: center;
}
</style>

<script>
import StartTrialFooter from "../../StartTrialFooter";
import OptionSetSelector from "../../OptionSetSelector";

export default {
  components: {
    StartTrialFooter,
    OptionSetSelector,
  },
  props: {
    model: {
      type: Object,
      default: () => null,
    },
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentPlan: "Free",
      rateLimits: {
        hourly: 20,
        daily: 100,
      },
      contentLimits: {
        maxRecipients: 10,
        maxBodySize: 51200, // 50KB
        maxAttachments: 3,
        maxAttachmentSize: 5242880, // 5MB
      },
    };
  },
  computed: {
    projectUUID() {
      return this.$store.getters.getCurrentProject.uuid;
    },
    tenantUUID() {
      // In production, this should come from project metadata
      return this.projectUUID;
    },
    senderEmail() {
      return `noreply@${this.tenantUUID}.mail.protokol.cloud`;
    },
  },
  watch: {
    active(newVal) {
      if (newVal) {
        this.loadPlanInfo();
      }
    },
  },
  created() {
    if (this.active) {
      this.loadPlanInfo();
    }
  },
  methods: {
    handleOptionSelect(option) {
      console.log('Option selected:', option);
    },

    handleCheckout(data) {
      console.log('Checkout for:', data);
      // TODO: Implement checkout redirect with option selection
      // For now, just log the data
      this.$bvToast.toast(
        this.$t('checkoutIntegrationWillBeImplementedSoon'),
        {
          title: this.$t('info'),
          variant: 'info'
        }
      );
    },

    handleUpdateOption(option) {
      console.log('Update option to:', option);
      // TODO: Implement option update via API
      this.$bvToast.toast(
        this.$t('optionUpdateWillBeImplementedSoon'),
        {
          title: this.$t('info'),
          variant: 'info'
        }
      );
    },

    loadPlanInfo() {
      // Extract plan info from model.option_set if available
      if (this.model && this.model.subscription && this.model.subscription.option_code) {
        this.currentPlan = this.formatPlanName(this.model.subscription.option_code);
        
        // Load limits based on plan
        const option = this.model.option_set && this.model.option_set.items && this.model.option_set.items.find(
          (opt) => opt.code === this.model.subscription.option_code
        );
        
        if (option && option.policies) {
          const policies = option.policies;
          
          // Rate limits (these would typically come from the backend via model)
          if (this.model.subscription.option_code === 'standard') {
            this.rateLimits = { hourly: 100, daily: 500 };
          } else if (this.model.subscription.option_code === 'pro') {
            this.rateLimits = { hourly: 500, daily: 2000 };
          }
          
          // Content limits from policies
          this.contentLimits = {
            maxRecipients: this.getPolicyValue(policies, 'max_recipients', 10),
            maxBodySize: this.getPolicyValue(policies, 'max_body_size', 51200),
            maxAttachments: this.getPolicyValue(policies, 'max_attachments', 3),
            maxAttachmentSize: this.getPolicyValue(policies, 'max_attachment_size', 5242880),
          };
        }
      }
    },
    
    getPolicyValue(policies, name, defaultValue) {
      const policy = policies.find((p) => p.name === name);
      return policy && policy.value !== undefined ? policy.value : defaultValue;
    },
    
    formatPlanName(code) {
      const names = {
        free: 'Free',
        standard: 'Standard',
        pro: 'Pro',
      };
      return names[code] || code;
    },
    
    formatBytes(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';
      
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    },
  },
};
</script>

